import axios, { AxiosError, AxiosInstance } from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAINAPP_URL = process.env.REACT_APP_MAINAPP_URL || '';

// Function to determine if we're using absolute or relative URLs
const getBaseUrl = (url: string) => {
  // If URL is provided and starts with http, use it as is
  if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
    return url;
  }
  
  // For the main app URL, we should never use relative URLs, even in production
  if (url === MAINAPP_URL && MAINAPP_URL) {
    return MAINAPP_URL;
  }
  
  // In production on Vercel, use relative URLs for internal backend calls
  if (process.env.NODE_ENV === 'production') {
    return '';
  }
  
  // Default to localhost in development
  return url || 'http://localhost:8000';
};

// Generic API response interface
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// Auth check response data
interface AuthResponseData {
  authenticated: boolean;
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

// Create axios instances with proper configuration
// Main application API client
const mainAppApi: AxiosInstance = axios.create({
  // Don't set baseURL here since we'll use the full URL in our requests
  // This avoids any issues with relative vs absolute URLs
  withCredentials: true, // Critical for session handling - this ensures cookies are sent
  headers: {
    'Content-Type': 'application/json',
  }
});

// YogAI backend API client
const backendApi: AxiosInstance = axios.create({
  baseURL: getBaseUrl(BACKEND_URL),
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add interceptor to handle authentication errors
mainAppApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      console.log('Authentication failed. Redirecting to login...');
      window.location.href = `${getBaseUrl(MAINAPP_URL)}/login?redirect=${encodeURIComponent(window.location.href)}`;
    }
    return Promise.reject(error);
  }
);

// API methods
const api = {
  // Course plans
  courses: {
    // Get course plan by ID with AI instructions
    getCoursePlan: async (courseId: string): Promise<any> => {
      // Ensure MAINAPP_URL is configured
      if (!MAINAPP_URL) {
        console.error('API: MAINAPP_URL is not configured. Please set the REACT_APP_MAINAPP_URL environment variable.');
        throw new Error('Main app URL is not configured. Please contact the administrator.');
      }
      
      // Ensure we're using the main app URL by constructing the full URL
      const mainAppBaseUrl = getBaseUrl(MAINAPP_URL);
      const fullUrl = `${mainAppBaseUrl}/api/course-plans/${courseId}/ai-instruction`;
      
      console.log(`API: Fetching course plan with ID ${courseId} from ${fullUrl}`);
      
      try {
        // Use the configured axios instance, but with an explicit URL
        const response = await mainAppApi.get(fullUrl);
        console.log('API: Received course plan response:', response.status);
        console.log('API: Response content type:', response.headers['content-type']);
        
        // Check if response data is valid
        if (!response.data) {
          console.error('API: Empty response data from course plan endpoint');
        } else {
          console.log('API: Response data keys:', Object.keys(response.data));
          console.log('API: Has course_plan?', response.data.hasOwnProperty('course_plan'));
        }
        
        return response.data;
      } catch (error: any) {
        console.error('API: Error fetching course plan:', error.message);
        console.error('API: Error details:', error.response?.data || 'No response data');
        throw error;
      }
    }
  }
};

export default api; 